import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Download,
  LayoutGrid,
  Loader2,
  Maximize2,
  Minus,
  Package,
  Plus,
  X,
} from 'lucide-react';
import { LookbookBrand, LookbookDesign } from '@/types/activation/lookbook';
import { LOOKBOOK_PAGE_H, LOOKBOOK_PAGE_W, LookbookPageFrame } from './LookbookPageFrame';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type ViewMode = 'grid' | 'focus';

interface LookbookImmersiveViewerProps {
  open: boolean;
  onClose: () => void;
  designs: LookbookDesign[];
  brand: LookbookBrand;
  /** The Genie's brand color (hex) — drives the ambient glow + CTA gradient. */
  brandColor?: string;
  initialIndex?: number;
  initialMode?: ViewMode;
  onDownload: () => void;
  isDownloading?: boolean;
  /** Add the picked designs to the pack's print order, each as its own PDF. Resolve `false` to keep the selection. */
  onAddToPrintOrder?: (designs: LookbookDesign[], quantity: number) => void | Promise<boolean | void>;
  isAddingToPrintOrder?: boolean;
}

const DEFAULT_SELECT_QTY = 10;

const INK = '#15233A';

const rgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/** Renders a full A4 lookbook page scaled to fit whatever box it's dropped into. */
const FitPage = ({
  brand,
  design,
  index,
  total,
}: {
  brand: LookbookBrand;
  design: LookbookDesign;
  index: number;
  total: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setBox({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = box.w && box.h ? Math.min(box.w / LOOKBOOK_PAGE_W, box.h / LOOKBOOK_PAGE_H) : 0;

  return (
    <div ref={ref} className="flex h-full w-full items-center justify-center">
      {scale > 0 && (
        <div
          style={{
            width: LOOKBOOK_PAGE_W * scale,
            height: LOOKBOOK_PAGE_H * scale,
            borderRadius: Math.max(6, 18 * scale),
            overflow: 'hidden',
            boxShadow: '0 30px 80px rgba(0,0,0,0.45)',
          }}
        >
          <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            <LookbookPageFrame brand={brand} design={design} index={index} total={total} />
          </div>
        </div>
      )}
    </div>
  );
};

const pageVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 64 : -64, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -64 : 64, opacity: 0 }),
};

export const LookbookImmersiveViewer = ({
  open,
  onClose,
  designs,
  brand,
  brandColor = '#4E9CFF',
  initialIndex = 0,
  initialMode = 'grid',
  onDownload,
  isDownloading = false,
  onAddToPrintOrder,
  isAddingToPrintOrder = false,
}: LookbookImmersiveViewerProps) => {
  const total = designs.length;
  const [mode, setMode] = useState<ViewMode>(initialMode);
  const [index, setIndex] = useState(initialIndex);
  const [dir, setDir] = useState(0);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectQty, setSelectQty] = useState(DEFAULT_SELECT_QTY);

  const stripRef = useRef<HTMLDivElement>(null);
  const activeThumbRef = useRef<HTMLButtonElement>(null);

  // Sync entry state each time the viewer opens.
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setIndex(initialIndex);
      setDir(0);
      setSelectMode(false);
      setSelected(new Set());
      setSelectQty(DEFAULT_SELECT_QTY);
    }
  }, [open, initialMode, initialIndex]);

  const toggleSelectMode = useCallback(() => {
    setSelectMode((on) => {
      if (!on) setMode('grid');
      else setSelected(new Set());
      return !on;
    });
  }, []);

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(designs.map((d) => d.id)));
  }, [designs]);

  const handleAddSelected = useCallback(async () => {
    if (!onAddToPrintOrder || selected.size === 0) return;
    const picked = designs.filter((d) => selected.has(d.id));
    const ok = await onAddToPrintOrder(picked, selectQty);
    // Leave the picks in place on failure so a retry is one click.
    if (ok === false) return;
    setSelectMode(false);
    setSelected(new Set());
  }, [onAddToPrintOrder, designs, selected, selectQty]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const go = useCallback(
    (delta: number) => {
      setDir(delta);
      setIndex((i) => (i + delta + total) % total);
    },
    [total],
  );

  const openFocus = useCallback((i: number) => {
    setDir(0);
    setIndex(i);
    setMode('focus');
  }, []);

  // Keyboard: Esc steps back (focus → grid → close); arrows navigate in focus.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (mode === 'focus') setMode('grid');
        else onClose();
      } else if (mode === 'focus' && e.key === 'ArrowRight') {
        go(1);
      } else if (mode === 'focus' && e.key === 'ArrowLeft') {
        go(-1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, mode, go, onClose]);

  // Keep the active thumbnail centered in the filmstrip.
  useEffect(() => {
    if (mode !== 'focus') return;
    activeThumbRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [index, mode]);

  const design = designs[index];

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Lookbook"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{
            background: `radial-gradient(1100px 640px at 12% -8%, ${rgba(brandColor, 0.22)}, transparent 60%), radial-gradient(1000px 600px at 112% 112%, ${rgba('#FF4DB8', 0.18)}, transparent 55%), ${INK}`,
          }}
        >
          {/* ── Header ───────────────────────────────────────────────────── */}
          <div className="relative z-10 flex items-center justify-between gap-3 px-4 py-4 sm:px-6 border-b border-white/10">
            <div className="flex min-w-0 items-center gap-3">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] p-[2px]">
                <div className="flex h-full w-full items-center justify-center rounded-[10px]" style={{ background: INK }}>
                  <GenieIcon name="book-open" size={20} color="#FFFFFF" label="Lookbook" />
                </div>
              </div>
              <div className="min-w-0">
                <div className="truncate text-base font-bold text-white">Lookbook</div>
                <div className="truncate text-xs text-white/55">{total} branded designs · {brand.name}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* View toggle */}
              <div className="hidden rounded-xl bg-white/10 p-1 sm:flex">
                {(['grid', 'focus'] as ViewMode[]).map((m) => {
                  const isActive = mode === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMode(m)}
                      className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all"
                      style={
                        isActive
                          ? { background: `linear-gradient(135deg, ${brandColor}, #FF4DB8)`, color: '#fff' }
                          : { color: 'rgba(255,255,255,0.6)' }
                      }
                    >
                      {m === 'grid' ? <LayoutGrid className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                      {m === 'grid' ? 'All designs' : 'Single'}
                    </button>
                  );
                })}
              </div>

              {onAddToPrintOrder && (
                <button
                  type="button"
                  onClick={toggleSelectMode}
                  className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-colors"
                  style={
                    selectMode
                      ? { background: 'rgba(255,255,255,0.16)', color: '#fff' }
                      : { background: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.75)' }
                  }
                >
                  <CheckSquare className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{selectMode ? 'Cancel' : 'Select designs'}</span>
                </button>
              )}

              <button
                type="button"
                onClick={onDownload}
                disabled={isDownloading}
                className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${brandColor}, #FF4DB8)` }}
              >
                {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span className="hidden sm:inline">{isDownloading ? 'Building…' : 'Download PDF'}</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close lookbook"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* ── Body ─────────────────────────────────────────────────────── */}
          {mode === 'grid' ? (
            <>
            <motion.div
              key="grid"
              className="relative z-10 flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mx-auto grid max-w-[1500px] grid-cols-2 gap-5 sm:grid-cols-3 sm:gap-6 xl:grid-cols-4">
                {designs.map((d, i) => {
                  const isSelected = selected.has(d.id);
                  return (
                    <motion.button
                      key={d.id}
                      type="button"
                      onClick={() => (selectMode ? toggleSelected(d.id) : openFocus(i))}
                      className="group text-left"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
                    >
                      <div
                        className="relative aspect-[794/1123] overflow-hidden rounded-2xl ring-1 ring-white/10 transition-all duration-300 group-hover:-translate-y-1 group-hover:ring-2"
                        style={{
                          ['--tw-ring-color' as string]: rgba(brandColor, 0.6),
                          ...(isSelected ? { boxShadow: `0 0 0 3px ${brandColor}` } : {}),
                        }}
                      >
                        <FitPage brand={brand} design={d} index={i + 1} total={total} />
                        {selectMode ? (
                          <div
                            className="absolute inset-0 transition-colors duration-200"
                            style={isSelected ? { background: rgba(brandColor, 0.18) } : undefined}
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <span className="mb-3 inline-flex items-center gap-1.5 rounded-lg bg-white/95 px-2.5 py-1.5 text-xs font-semibold text-gray-800 shadow">
                              <Maximize2 className="h-3.5 w-3.5" />
                              View
                            </span>
                          </div>
                        )}
                        {selectMode && (
                          <div
                            className="absolute left-2.5 top-2.5 flex h-6 w-6 items-center justify-center rounded-md border-2 backdrop-blur-sm transition-all"
                            style={
                              isSelected
                                ? { background: brandColor, borderColor: brandColor }
                                : { background: 'rgba(0,0,0,0.35)', borderColor: 'rgba(255,255,255,0.7)' }
                            }
                          >
                            {isSelected && <Check className="h-4 w-4 text-white" />}
                          </div>
                        )}
                      </div>
                      <div className="mt-2.5">
                        <div className="text-sm font-semibold text-white">
                          <span className="text-white/40">{String(i + 1).padStart(2, '0')}</span> {d.name}
                        </div>
                        <div className="mt-0.5 text-xs text-white/45">{d.meta}</div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>

            {selectMode && (
              <div className="relative z-10 border-t border-white/10 bg-black/30 px-4 py-3 backdrop-blur-sm sm:px-8">
                <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-white">
                      {selected.size} selected
                    </span>
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-xs font-semibold text-white/60 transition-colors hover:text-white"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelected(new Set())}
                      className="text-xs font-semibold text-white/60 transition-colors hover:text-white"
                    >
                      Clear
                    </button>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 rounded-lg bg-white/10 px-1.5 py-1">
                      <button
                        type="button"
                        onClick={() => setSelectQty((q) => Math.max(1, q - 1))}
                        aria-label="Decrease quantity"
                        className="flex h-6 w-6 items-center justify-center rounded text-white/80 transition-colors hover:bg-white/10"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-9 text-center text-sm font-semibold text-white">{selectQty}</span>
                      <button
                        type="button"
                        onClick={() => setSelectQty((q) => q + 1)}
                        aria-label="Increase quantity"
                        className="flex h-6 w-6 items-center justify-center rounded text-white/80 transition-colors hover:bg-white/10"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleAddSelected}
                      disabled={selected.size === 0 || isAddingToPrintOrder}
                      className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                      style={{ background: `linear-gradient(135deg, ${brandColor}, #FF4DB8)` }}
                    >
                      {isAddingToPrintOrder ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Package className="h-4 w-4" />
                      )}
                      {isAddingToPrintOrder ? 'Adding…' : 'Add to print order'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            </>
          ) : (
            <div className="relative z-10 flex flex-1 flex-col overflow-hidden">
              {/* Stage */}
              <div className="relative flex-1 px-14 py-5 sm:px-24 sm:py-7">
                <AnimatePresence custom={dir} initial={false} mode="popLayout">
                  <motion.div
                    key={index}
                    custom={dir}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0"
                  >
                    <FitPage brand={brand} design={design} index={index + 1} total={total} />
                  </motion.div>
                </AnimatePresence>

                {/* Arrows */}
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Previous design"
                  className="absolute left-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:left-6"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Next design"
                  className="absolute right-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:right-6"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>

              {/* Caption */}
              <div className="text-center">
                <div className="text-base font-semibold text-white">
                  {design.name}
                  <span className="font-normal text-white/40"> · {index + 1} of {total}</span>
                </div>
                <div className="mt-0.5 text-xs text-white/50">{design.meta}</div>
              </div>

              {/* Filmstrip */}
              <div ref={stripRef} className="flex items-center gap-2.5 overflow-x-auto px-4 py-5 sm:px-8">
                <div className="mx-auto flex gap-2.5">
                  {designs.map((d, i) => {
                    const isActive = i === index;
                    return (
                      <button
                        key={d.id}
                        ref={isActive ? activeThumbRef : undefined}
                        type="button"
                        onClick={() => {
                          setDir(i > index ? 1 : -1);
                          setIndex(i);
                        }}
                        aria-label={d.name}
                        className="aspect-[794/1123] h-20 shrink-0 overflow-hidden rounded-lg transition-all"
                        style={{
                          padding: 2,
                          background: isActive ? `linear-gradient(135deg, ${brandColor}, #FF4DB8)` : 'transparent',
                          opacity: isActive ? 1 : 0.5,
                        }}
                      >
                        <div className="h-full w-full overflow-hidden rounded-[6px]">
                          <FitPage brand={brand} design={d} index={i + 1} total={total} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
